import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  Search,
  Filter,
  TrendingUp,
  TrendingDown,
  Edit,
  Trash2,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTransactions, useAccounts, useDeleteTransaction } from '@/hooks/queries/useTreasury';
import { TransactionForm } from '@/features/treasury/components/TransactionForm';
import { useState } from 'react';
import { toast } from '@/components/ui/toast/useToast';
import type { Transaction } from '@/types/treasury';

export const Route = createFileRoute('/apps/treasury/transactions/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: TransactionsPage,
});

function TransactionsPage() {
  const navigate = useNavigate();
  const { company } = useAuthStore();
  const { data: transactions = [], isLoading, refetch } = useTransactions(company?.id || '');
  const { data: accounts = [] } = useAccounts(company?.id || '');
  const deleteTransaction = useDeleteTransaction();

  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | undefined>();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterAccount, setFilterAccount] = useState<string>('all');
  const [filterPayment, setFilterPayment] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-DZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleDelete = async (transaction: Transaction) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer cette transaction?`)) {
      try {
        await deleteTransaction.mutateAsync(transaction.id);
        toast.success('Transaction supprimée', 'La transaction a été supprimée avec succès.');
        refetch();
      } catch (error) {
        toast.error('Erreur', 'Impossible de supprimer la transaction.');
      }
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter((transaction) => {
    // Search filter
    if (searchQuery && !transaction.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Type filter
    if (filterType !== 'all' && transaction.type !== filterType) {
      return false;
    }

    // Account filter
    if (filterAccount !== 'all' && transaction.account_id !== filterAccount) {
      return false;
    }

    // Payment method filter
    if (filterPayment !== 'all' && transaction.payment_method !== filterPayment) {
      return false;
    }

    // Date range filter
    if (dateFrom && transaction.transaction_date < dateFrom) {
      return false;
    }
    if (dateTo && transaction.transaction_date > dateTo) {
      return false;
    }

    return true;
  });

  // Calculate totals for filtered transactions
  const totals = filteredTransactions.reduce(
    (acc, t) => {
      if (t.type === 'income') {
        acc.income += t.amount;
        acc.count.income++;
      } else {
        acc.expense += t.amount;
        acc.count.expense++;
      }
      return acc;
    },
    { income: 0, expense: 0, count: { income: 0, expense: 0 } }
  );

  // Export to Excel
  const exportToExcel = async () => {
    try {
      // Dynamic import to avoid build issues
      const XLSX = await import('xlsx');
      
      // Prepare data for export
      const exportData = filteredTransactions.map((t) => {
        const account = accounts.find((a) => a.id === t.account_id);
        return {
          'Date': formatDate(t.transaction_date),
          'N° Transaction': t.transaction_number,
          'Type': t.type === 'income' ? 'Entrée' : 'Sortie',
          'Compte': account?.name || '',
          'Description': t.description,
          'Catégorie': t.category || '',
          'Moyen de paiement': t.payment_method,
          'Montant (DA)': t.amount,
          'Contact': t.contact_name || '',
          'Notes': t.notes || '',
        };
      });

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const colWidths = [
        { wch: 12 }, // Date
        { wch: 20 }, // Transaction #
        { wch: 10 }, // Type
        { wch: 20 }, // Account
        { wch: 30 }, // Description
        { wch: 15 }, // Category
        { wch: 18 }, // Payment method
        { wch: 15 }, // Amount
        { wch: 20 }, // Contact
        { wch: 25 }, // Notes
      ];
      worksheet['!cols'] = colWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

      // Add summary sheet
      const summaryData = [
        { Label: 'Période', Valeur: `${dateFrom || 'Début'} - ${dateTo || "Aujourd'hui"}` },
        { Label: 'Total Entrées', Valeur: `${formatCurrency(totals.income)} (${totals.count.income} transactions)` },
        { Label: 'Total Sorties', Valeur: `${formatCurrency(totals.expense)} (${totals.count.expense} transactions)` },
        { Label: 'Solde Net', Valeur: formatCurrency(totals.income - totals.expense) },
        { Label: 'Total Transactions', Valeur: filteredTransactions.length },
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Résumé');

      // Generate filename with date
      const today = new Date().toISOString().split('T')[0];
      const filename = `transactions_${today}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      toast.success('Export réussi', `Fichier ${filename} téléchargé avec succès.`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur', "Erreur lors de l'export. Assurez-vous que xlsx est installé.");
    }
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const XLSX = await import('xlsx');
      
      const exportData = filteredTransactions.map((t) => {
        const account = accounts.find((a) => a.id === t.account_id);
        return {
          'Date': formatDate(t.transaction_date),
          'N° Transaction': t.transaction_number,
          'Type': t.type === 'income' ? 'Entrée' : 'Sortie',
          'Compte': account?.name || '',
          'Description': t.description,
          'Catégorie': t.category || '',
          'Moyen de paiement': t.payment_method,
          'Montant (DA)': t.amount,
          'Contact': t.contact_name || '',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const csv = XLSX.utils.sheet_to_csv(worksheet);

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Export réussi', 'Fichier CSV téléchargé avec succès.');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur', "Erreur lors de l'export.");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate({ to: '/apps/treasury' })}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              المعاملات / Transactions
            </h1>
            <p className="mt-2 text-gray-600">
              {filteredTransactions.length} transaction(s)
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refetch}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={exportToExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Entrées</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(totals.income)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {totals.count.income} transaction(s)
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <TrendingUp className="w-8 h-8 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Sorties</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(totals.expense)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {totals.count.expense} transaction(s)
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-xl">
                  <TrendingDown className="w-8 h-8 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Solde Net</p>
                  <p className={`text-2xl font-bold ${totals.income - totals.expense >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(totals.income - totals.expense)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Pour cette période
                  </p>
                </div>
                <div className={`p-3 rounded-xl ${totals.income - totals.expense >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <Calendar className={`w-8 h-8 ${totals.income - totals.expense >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtres
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Recherche
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Type
                </label>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="income">Entrées</SelectItem>
                    <SelectItem value="expense">Sorties</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Compte
                </label>
                <Select value={filterAccount} onValueChange={setFilterAccount}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Payment Method Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Paiement
                </label>
                <Select value={filterPayment} onValueChange={setFilterPayment}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="check">Chèque</SelectItem>
                    <SelectItem value="transfer">Virement</SelectItem>
                    <SelectItem value="card">Carte</SelectItem>
                    <SelectItem value="ccp">CCP</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date Range (spans 2 columns on large screens) */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-2">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Du
                  </label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Au
                  </label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || filterType !== 'all' || filterAccount !== 'all' || filterPayment !== 'all' || dateFrom || dateTo) && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                    setFilterAccount('all');
                    setFilterPayment('all');
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Effacer les filtres
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">
                Chargement...
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="font-medium">Aucune transaction trouvée</p>
                <p className="text-sm mt-1">
                  {transactions.length === 0
                    ? 'Créez votre première transaction'
                    : 'Modifiez vos filtres'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        N° Transaction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Compte
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Paiement
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredTransactions.map((transaction) => {
                      const account = accounts.find((a) => a.id === transaction.account_id);
                      
                      return (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(transaction.transaction_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {transaction.transaction_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <div
                                className={`p-1 rounded ${
                                  transaction.type === 'income'
                                    ? 'bg-emerald-100 text-emerald-600'
                                    : 'bg-red-100 text-red-600'
                                }`}
                              >
                                {transaction.type === 'income' ? (
                                  <TrendingUp className="w-4 h-4" />
                                ) : (
                                  <TrendingDown className="w-4 h-4" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{transaction.description}</p>
                                {transaction.contact_name && (
                                  <p className="text-xs text-gray-500">
                                    {transaction.contact_name}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account?.name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                            {transaction.payment_method}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold">
                            <span
                              className={
                                transaction.type === 'income'
                                  ? 'text-emerald-600'
                                  : 'text-red-600'
                              }
                            >
                              {transaction.type === 'income' ? '+' : '-'}
                              {formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingTransaction(transaction);
                                  setShowTransactionForm(true);
                                }}
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(transaction)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transaction Form Dialog */}
        <TransactionForm
          open={showTransactionForm}
          onClose={() => {
            setShowTransactionForm(false);
            setEditingTransaction(undefined);
            refetch();
          }}
          transaction={editingTransaction}
        />
      </div>
    </DashboardLayout>
  );
}
